import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, GitMerge } from 'lucide-react';
import { PageHeader, EmptyState } from '@/components/shared/page';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/loader';
import { ApiRequestError, apiGet, apiPost } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

type Dup = {
  id: string;
  firstName?: string;
  lastName?: string | null;
  name?: string;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  trn?: string | null;
  createdAt: string;
  company?: { name: string } | null;
};
type Group = { reason: string; records: Dup[] };

/**
 * Manage duplicates (HubSpot data quality): pairs of contacts or companies
 * that look like the same person or business, merged in one click with the
 * record that stays chosen by you.
 */
export function DuplicatesPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const type = params.get('type') === 'company' ? 'company' : 'contact';
  const queryClient = useQueryClient();
  const { data: groups = [], isLoading } = useQuery({ queryKey: ['records', type, 'duplicates'], queryFn: () => apiGet<Group[]>(`/records/${type}/duplicates`) });
  const [keep, setKeep] = useState<Record<string, string>>({});

  const merge = useMutation({
    mutationFn: (body: { primaryId: string; duplicateId: string }) => apiPost<{ moved: number; filled: string[] }>(`/records/${type}/merge`, body),
    onSuccess: (result) => {
      toast.success(t('Merged. {count} linked items moved across.', { count: result.moved }));
      void queryClient.invalidateQueries({ queryKey: ['records', type, 'duplicates'] });
      void queryClient.invalidateQueries({ queryKey: [type === 'contact' ? 'contacts' : 'companies'] });
    },
    onError: (error) => toast.error(error instanceof ApiRequestError ? error.message : 'Could not merge'),
  });

  const title = (record: Dup) => (type === 'contact' ? [record.firstName, record.lastName].filter(Boolean).join(' ') : record.name);
  const detail = (record: Dup) =>
    (type === 'contact' ? [record.email, record.phone, record.company?.name] : [record.website, record.trn]).filter(Boolean).join(' · ');

  return (
    <div>
      <PageHeader
        title="Manage duplicates"
        description="Records that share an email, phone, name, website or TRN. Pick the one to keep; everything linked to the other moves across."
        breadcrumbs={[{ label: type === 'contact' ? 'Contacts' : 'Companies', to: type === 'contact' ? '/crm/contacts' : '/crm/companies' }, { label: 'Duplicates' }]}
      />
      <div className="mb-4 flex gap-1 rounded-lg border bg-card p-1">
        {(['contact', 'company'] as const).map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setParams({ type: item })}
            className={cn('flex-1 rounded-md px-3 py-2 text-sm font-semibold', type === item ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900' : 'text-muted-foreground hover:bg-muted')}
          >
            {item === 'contact' ? t('Contacts') : t('Companies')}
          </button>
        ))}
      </div>
      {isLoading ? (
        <PageLoader />
      ) : groups.length === 0 ? (
        <EmptyState icon={<Copy className="h-6 w-6" />} title="No duplicates found" description="Your records look clean. New duplicates show up here as they appear." />
      ) : (
        <div className="space-y-3">
          {groups.map((group) => {
            const key = group.records.map((record) => record.id).join('|');
            const primaryId = keep[key] ?? group.records[0].id;
            return (
              <div key={key} className="rounded-lg border bg-card p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">{t(group.reason)}</span>
                  <span className="text-xs text-muted-foreground">{t('Choose the record to keep')}</span>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {group.records.map((record) => (
                    <label
                      key={record.id}
                      className={cn('flex cursor-pointer items-start gap-3 rounded-md border p-3', primaryId === record.id && 'border-primary bg-primary/[0.05] ring-1 ring-primary')}
                    >
                      <input type="radio" name={key} checked={primaryId === record.id} onChange={() => setKeep({ ...keep, [key]: record.id })} className="mt-1 accent-[hsl(var(--primary))]" />
                      <span className="min-w-0">
                        <Link to={`/crm/${type === 'contact' ? 'contacts' : 'companies'}/${record.id}`} className="text-link font-semibold hover:underline">
                          {title(record)}
                        </Link>
                        <span className="block truncate text-xs text-muted-foreground">{detail(record) || '--'}</span>
                        <span className="block text-xs text-muted-foreground">
                          {t('Created')} {formatDate(record.createdAt)}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  {group.records
                    .filter((record) => record.id !== primaryId)
                    .map((record) => (
                      <Button
                        key={record.id}
                        size="sm"
                        disabled={merge.isPending}
                        onClick={() => {
                          if (window.confirm(t('Merge "{other}" into "{keep}"? This cannot be undone.', { other: title(record) ?? '', keep: title(group.records.find((item) => item.id === primaryId)!) ?? '' }))) {
                            merge.mutate({ primaryId, duplicateId: record.id });
                          }
                        }}
                      >
                        <GitMerge /> {t('Merge {name} into the chosen record', { name: title(record) ?? '' })}
                      </Button>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
